(function (global) {
  'use strict';

  if (global.CmxHomeCollageSlider) return;

  var MOBILE_MAX_WIDTH = 749;
  var TABLET_MAX_WIDTH = 989;
  var SWIPE_THRESHOLD = 40;
  var POINTER_DRAG_START_THRESHOLD = 6;
  var POINTER_DRAG_COMMIT_THRESHOLD = 50;
  var MIN_VIDEO_SLIDE_DELAY = 2000;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getPerView(width, configuredPerView) {
    if (width <= MOBILE_MAX_WIDTH) return 1;
    if (width <= TABLET_MAX_WIDTH) return 2;

    return Math.max(1, parseInt(configuredPerView, 10) || 3);
  }

  function getMaxIndex(total, perView, scrollByPage) {
    if (scrollByPage) {
      return Math.max(0, Math.ceil(total / perView) - 1);
    }

    return Math.max(0, total - perView);
  }

  function normalizeIndex(index, maxIndex, wrap) {
    if (!wrap) return clamp(index, 0, maxIndex);

    return ((index % (maxIndex + 1)) + (maxIndex + 1)) % (maxIndex + 1);
  }

  function getIndexAfterResize(index, maxIndex) {
    return clamp(index, 0, maxIndex);
  }

  function isHorizontalSwipe(startX, startY, endX, endY, threshold) {
    var deltaX = endX - startX;
    var deltaY = endY - startY;
    var minimumDistance = threshold || SWIPE_THRESHOLD;

    return Math.abs(deltaX) > minimumDistance && Math.abs(deltaX) > Math.abs(deltaY);
  }

  function getDragStepCount(deltaX, positionStep, threshold) {
    var distance = Math.abs(deltaX);
    var minimumDistance = threshold || POINTER_DRAG_COMMIT_THRESHOLD;
    if (distance < minimumDistance) return 0;
    if (!positionStep || positionStep <= 0) return 1;

    return Math.max(1, Math.round(distance / positionStep));
  }

  function shouldAutoplay(options) {
    return Boolean(
      options.enabled &&
      options.total > options.perView &&
      !options.userPaused &&
      !options.reducedMotion &&
      !options.transientlyPaused
    );
  }

  function shouldPlayVideo(options) {
    return Boolean(
      options.panelVisible &&
      options.sectionVisible &&
      !options.documentHidden &&
      !options.reducedMotion &&
      !options.saveData
    );
  }

  function getAutoplayDelay(baseInterval, videoRemaining) {
    if (!Number.isFinite(videoRemaining) || videoRemaining <= 0) return baseInterval;

    return Math.max(MIN_VIDEO_SLIDE_DELAY, Math.round(videoRemaining));
  }

  function getPanelPosition(panelIndex, perView, scrollByPage) {
    return scrollByPage ? Math.floor(panelIndex / Math.max(1, perView)) : panelIndex;
  }

  function prefersReducedData() {
    return Boolean(global.navigator?.connection?.saveData);
  }

  function requestFrame(callback) {
    if (typeof global.requestAnimationFrame === 'function') {
      return global.requestAnimationFrame(callback);
    }

    return global.setTimeout(callback, 0);
  }

  class CmxHomeCollageSliderElement extends global.HTMLElement {
    constructor() {
      super();

      this.initialized = false;
      this.index = 0;
      this.timer = null;
      this.userPaused = false;
      this.transientPauses = {};
      this.touchStart = null;
      this.pointerDrag = null;
      this.suppressClick = false;
      this.suppressClickTimer = null;
      this.lastViewportWidth = 0;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.motionQuery = null;
      this.instantFrame = null;
      this.videoSlots = [];
      this.videoSyncEnabled = true;
      this.sectionInViewport = true;
      this.firstVisiblePanel = 0;
      this.visiblePanelCount = 0;

      this.handleDotsClick = this.handleDotsClick.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handlePointerCancel = this.handlePointerCancel.bind(this);
      this.handleTrackClickCapture = this.handleTrackClickCapture.bind(this);
      this.handleNativeDragStart = this.handleNativeDragStart.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleTouchCancel = this.handleTouchCancel.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
      this.handleWindowResize = this.handleWindowResize.bind(this);
      this.handleVideoPlaying = this.handleVideoPlaying.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-chcs-track]');
      this.viewport = this.querySelector('.chcs__viewport');
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-chcs-panel]'));
      this.dotsWrap = this.querySelector('[data-chcs-dots]');
      if (!this.track || !this.viewport || this.panels.length === 0) return;

      this.autoplayEnabled = this.dataset.autoplay === '1';
      this.autoplayInterval = Math.max(1000, parseInt(this.dataset.interval, 10) || 6000);
      this.videoSyncEnabled = this.dataset.videoSync !== '0';
      this.scrollByPage = this.dataset.scrollPage === '1';
      this.configuredPerView = Math.max(1, parseInt(this.dataset.perView, 10) || 3);
      this.lastViewportWidth = this.getViewportWidth();
      this.motionQuery = global.matchMedia('(prefers-reduced-motion: reduce)');
      this.videoSlots = this.collectVideoSlots();
      this.initialized = true;

      this.addEventListeners();
      this.setupObservers();
      this.go(0, { wrap: false, instant: true });
      this.syncAutoplay();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.clearTimer();
      if (this.suppressClickTimer !== null) global.clearTimeout(this.suppressClickTimer);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.unmountVideos();

      this.dotsWrap?.removeEventListener('click', this.handleDotsClick);
      this.track?.removeEventListener('pointerdown', this.handlePointerDown);
      this.track?.removeEventListener('pointermove', this.handlePointerMove);
      this.track?.removeEventListener('pointerup', this.handlePointerUp);
      this.track?.removeEventListener('pointercancel', this.handlePointerCancel);
      this.track?.removeEventListener('click', this.handleTrackClickCapture, true);
      this.track?.removeEventListener('dragstart', this.handleNativeDragStart);
      this.track?.removeEventListener('touchstart', this.handleTouchStart);
      this.track?.removeEventListener('touchend', this.handleTouchEnd);
      this.track?.removeEventListener('touchcancel', this.handleTouchCancel);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      global.document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      global.removeEventListener('resize', this.handleWindowResize);

      if (this.motionQuery) {
        if (typeof this.motionQuery.removeEventListener === 'function') {
          this.motionQuery.removeEventListener('change', this.handleMotionPreferenceChange);
        } else if (typeof this.motionQuery.removeListener === 'function') {
          this.motionQuery.removeListener(this.handleMotionPreferenceChange);
        }
      }

      this.initialized = false;
    }

    getViewportWidth() {
      var rect = this.viewport?.getBoundingClientRect();
      return Math.round(rect?.width || this.viewport?.clientWidth || global.innerWidth || 0);
    }

    getPerView() {
      return getPerView(global.innerWidth || this.getViewportWidth(), this.configuredPerView);
    }

    getMaxIndex() {
      return getMaxIndex(this.panels.length, this.getPerView(), this.scrollByPage);
    }

    addEventListeners() {
      this.dotsWrap?.addEventListener('click', this.handleDotsClick);
      this.track.addEventListener('pointerdown', this.handlePointerDown);
      this.track.addEventListener('pointermove', this.handlePointerMove);
      this.track.addEventListener('pointerup', this.handlePointerUp);
      this.track.addEventListener('pointercancel', this.handlePointerCancel);
      this.track.addEventListener('click', this.handleTrackClickCapture, true);
      this.track.addEventListener('dragstart', this.handleNativeDragStart);
      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.handleTouchEnd, { passive: false });
      this.track.addEventListener('touchcancel', this.handleTouchCancel, { passive: true });
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      global.document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (typeof this.motionQuery.addEventListener === 'function') {
        this.motionQuery.addEventListener('change', this.handleMotionPreferenceChange);
      } else if (typeof this.motionQuery.addListener === 'function') {
        this.motionQuery.addListener(this.handleMotionPreferenceChange);
      }
    }

    setupObservers() {
      if (typeof global.ResizeObserver === 'function') {
        this.resizeObserver = new global.ResizeObserver((entries) => {
          var width = Math.round(entries[0]?.contentRect?.width || this.getViewportWidth());
          this.handleViewportResize(width);
        });
        this.resizeObserver.observe(this.viewport);
      } else {
        global.addEventListener('resize', this.handleWindowResize);
      }

      if (typeof global.IntersectionObserver === 'function') {
        this.sectionInViewport = false;
        this.setTransientPause('outsideViewport', true, false);
        this.intersectionObserver = new global.IntersectionObserver(
          (entries) => {
            var entry = entries[entries.length - 1];
            this.sectionInViewport = Boolean(entry?.isIntersecting);
            this.setTransientPause('outsideViewport', !this.sectionInViewport);
            this.syncVideos();
          },
          { threshold: 0.1 }
        );
        this.intersectionObserver.observe(this);
      }

      this.setTransientPause('documentHidden', Boolean(global.document.hidden), false);
    }

    go(value, options) {
      var config = options || {};
      var perView = this.getPerView();
      var maxIndex = this.getMaxIndex();
      var wrap = config.wrap !== false;
      this.index = normalizeIndex(value, maxIndex, wrap);

      var step = this.scrollByPage ? perView : 1;
      var targetIndex = Math.min(this.index * step, this.panels.length - 1);
      var targetPanel = this.panels[targetIndex];
      var offset = targetPanel?.offsetLeft || 0;

      if (config.instant) this.beginInstantPositioning();
      this.track.style.transform = 'translateX(-' + offset + 'px)';

      this.renderDots();
      this.updatePanelAccessibility(targetIndex, perView);
      this.firstVisiblePanel = targetIndex;
      this.visiblePanelCount = perView;
      this.syncVideos();
    }

    beginInstantPositioning() {
      this.setAttribute('data-chcs-instant', '');
      if (this.instantFrame && typeof global.cancelAnimationFrame === 'function') {
        global.cancelAnimationFrame(this.instantFrame);
      }

      this.instantFrame = requestFrame(() => {
        this.removeAttribute('data-chcs-instant');
        this.instantFrame = null;
      });
    }

    renderDots() {
      if (!this.dotsWrap) return;

      var count = this.getMaxIndex() + 1;
      if (this.dotsWrap.children.length !== count) {
        this.dotsWrap.innerHTML = '';
        for (var dotIndex = 0; dotIndex < count; dotIndex += 1) {
          var dot = global.document.createElement('button');
          dot.type = 'button';
          dot.className = 'chcs__dot';
          dot.dataset.chcsDot = String(dotIndex);
          dot.setAttribute('aria-label', 'Mostrar posición ' + (dotIndex + 1) + ' de ' + count);
          this.dotsWrap.appendChild(dot);
        }
      }

      Array.prototype.forEach.call(this.dotsWrap.children, (dot, dotIndex) => {
        var active = dotIndex === this.index;
        dot.classList.toggle('chcs__dot--active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    updatePanelAccessibility(targetIndex, perView) {
      this.panels.forEach((panel, panelIndex) => {
        var visible = panelIndex >= targetIndex && panelIndex < targetIndex + perView;
        panel.setAttribute('aria-hidden', visible ? 'false' : 'true');
        panel.toggleAttribute('inert', !visible);
      });
    }

    clearTimer() {
      if (this.timer !== null) {
        global.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    isTransientlyPaused() {
      return Object.keys(this.transientPauses).some((reason) => this.transientPauses[reason]);
    }

    syncAutoplay() {
      this.clearTimer();

      var autoplay = shouldAutoplay({
        enabled: this.autoplayEnabled,
        total: this.panels.length,
        perView: this.getPerView(),
        userPaused: this.userPaused,
        reducedMotion: this.motionQuery.matches,
        transientlyPaused: this.isTransientlyPaused(),
      });

      this.track.setAttribute('aria-live', autoplay ? 'off' : 'polite');

      if (!autoplay) return;

      this.timer = global.setTimeout(() => {
        this.timer = null;
        this.go(this.index + 1, { wrap: true });
        this.syncAutoplay();
      }, this.getAutoplayDelay());
    }

    getAutoplayDelay() {
      var remaining = this.videoSyncEnabled ? this.getPrincipalVideoRemaining() : 0;
      return getAutoplayDelay(this.autoplayInterval, remaining);
    }

    getPrincipalVideoRemaining() {
      var remaining = 0;
      this.videoSlots.forEach((slot) => {
        var video = slot.video;
        if (slot.panelIndex !== this.firstVisiblePanel || !video || video.paused) return;
        if (!Number.isFinite(video.duration) || video.duration <= 0) return;

        remaining = Math.max(remaining, (video.duration - video.currentTime) * 1000);
      });

      return remaining;
    }

    pauseByUser() {
      this.userPaused = true;
      this.syncAutoplay();
    }

    setTransientPause(reason, paused, sync) {
      this.transientPauses[reason] = paused;
      if (sync !== false) this.syncAutoplay();
    }

    handleDotsClick(event) {
      var dot = event.target.closest('[data-chcs-dot]');
      if (!dot || !this.dotsWrap.contains(dot)) return;

      event.preventDefault();
      this.pauseByUser();
      this.go(parseInt(dot.dataset.chcsDot, 10), { wrap: false });
    }

    getCurrentOffset() {
      var step = this.scrollByPage ? this.getPerView() : 1;
      var targetIndex = Math.min(this.index * step, this.panels.length - 1);
      return this.panels[targetIndex]?.offsetLeft || 0;
    }

    getPositionStep() {
      if (this.panels.length < 2) return this.viewport?.clientWidth || 1;

      var panelStep = Math.abs(this.panels[1].offsetLeft - this.panels[0].offsetLeft);
      if (!panelStep) panelStep = this.panels[0].offsetWidth || this.viewport?.clientWidth || 1;

      return panelStep * (this.scrollByPage ? this.getPerView() : 1);
    }

    handlePointerDown(event) {
      if (event.pointerType === 'touch' || event.button !== 0 || event.isPrimary === false) return;

      this.pointerDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        currentX: event.clientX,
        startOffset: this.getCurrentOffset(),
        positionStep: this.getPositionStep(),
        dragging: false,
      };
      this.setTransientPause('pointerDrag', true);
    }

    handlePointerMove(event) {
      var drag = this.pointerDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;

      drag.currentX = event.clientX;
      var deltaX = event.clientX - drag.startX;
      var deltaY = event.clientY - drag.startY;

      if (!drag.dragging) {
        if (Math.abs(deltaX) <= POINTER_DRAG_START_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;

        drag.dragging = true;
        this.track.setPointerCapture?.(drag.pointerId);
        this.pauseByUser();
        this.setAttribute('data-chcs-dragging', '');
      }

      event.preventDefault();
      var maxIndex = this.getMaxIndex();
      var backwardDistance = this.index * drag.positionStep;
      var forwardDistance = (maxIndex - this.index) * drag.positionStep;
      var renderedDelta = deltaX;
      if (deltaX > backwardDistance) {
        renderedDelta = backwardDistance + ((deltaX - backwardDistance) * 0.25);
      } else if (-deltaX > forwardDistance) {
        renderedDelta = -forwardDistance + ((deltaX + forwardDistance) * 0.25);
      }
      this.track.style.transform = 'translateX(' + (-drag.startOffset + renderedDelta) + 'px)';
    }

    handlePointerUp(event) {
      this.finishPointerDrag(event, false);
    }

    handlePointerCancel(event) {
      this.finishPointerDrag(event, true);
    }

    finishPointerDrag(event, cancelled) {
      var drag = this.pointerDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;

      if (this.track.hasPointerCapture?.(drag.pointerId)) {
        this.track.releasePointerCapture(drag.pointerId);
      }
      this.pointerDrag = null;
      this.setTransientPause('pointerDrag', false, false);

      if (!drag.dragging) {
        this.syncAutoplay();
        return;
      }

      event.preventDefault();
      this.removeAttribute('data-chcs-dragging');
      void this.track.offsetWidth;

      var deltaX = (Number.isFinite(event.clientX) ? event.clientX : drag.currentX) - drag.startX;
      var targetIndex = this.index;
      var stepCount = cancelled
        ? 0
        : getDragStepCount(deltaX, drag.positionStep, POINTER_DRAG_COMMIT_THRESHOLD);
      if (stepCount > 0) {
        targetIndex += deltaX < 0 ? stepCount : -stepCount;
      }

      this.go(targetIndex, { wrap: false });
      this.syncAutoplay();
      this.suppressClick = true;
      if (this.suppressClickTimer !== null) global.clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = global.setTimeout(() => {
        this.suppressClick = false;
        this.suppressClickTimer = null;
      }, 0);
    }

    handleTrackClickCapture(event) {
      if (!this.suppressClick) return;

      event.preventDefault();
      event.stopImmediatePropagation?.();
      event.stopPropagation();
      this.suppressClick = false;
      if (this.suppressClickTimer !== null) {
        global.clearTimeout(this.suppressClickTimer);
        this.suppressClickTimer = null;
      }
    }

    handleNativeDragStart(event) {
      event.preventDefault();
    }

    handleTouchStart(event) {
      if (event.touches.length !== 1) return;

      this.touchStart = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      this.setTransientPause('touch', true);
    }

    handleTouchEnd(event) {
      if (!this.touchStart || !event.changedTouches.length) {
        this.handleTouchCancel();
        return;
      }

      var endX = event.changedTouches[0].clientX;
      var endY = event.changedTouches[0].clientY;
      var horizontalSwipe = isHorizontalSwipe(
        this.touchStart.x,
        this.touchStart.y,
        endX,
        endY,
        SWIPE_THRESHOLD
      );
      var deltaX = endX - this.touchStart.x;

      this.touchStart = null;
      this.setTransientPause('touch', false, false);

      if (!horizontalSwipe) {
        this.syncAutoplay();
        return;
      }

      event.preventDefault();
      this.pauseByUser();
      this.go(this.index + (deltaX < 0 ? 1 : -1), { wrap: true });
    }

    handleTouchCancel() {
      this.touchStart = null;
      this.setTransientPause('touch', false);
    }

    handleMouseEnter() {
      this.setTransientPause('hover', true);
    }

    handleMouseLeave() {
      this.setTransientPause('hover', false);
    }

    handleFocusIn() {
      this.setTransientPause('focus', true);
    }

    handleFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.setTransientPause('focus', false);
    }

    handleVisibilityChange() {
      this.setTransientPause('documentHidden', Boolean(global.document.hidden));
      this.syncVideos();
    }

    handleMotionPreferenceChange() {
      this.syncAutoplay();
      this.syncVideos();
    }

    handleWindowResize() {
      this.handleViewportResize(this.getViewportWidth());
    }

    handleViewportResize(width) {
      if (!width || width === this.lastViewportWidth) return;

      this.lastViewportWidth = width;
      this.index = getIndexAfterResize(this.index, this.getMaxIndex());
      this.go(this.index, { wrap: false, instant: true });
      this.syncAutoplay();
    }

    collectVideoSlots() {
      var slots = [];
      this.panels.forEach((panel, panelIndex) => {
        Array.prototype.forEach.call(panel.querySelectorAll('template[data-chcs-video]'), (template) => {
          slots.push({ panelIndex: panelIndex, template: template, video: null });
        });
      });

      return slots;
    }

    syncVideos() {
      if (this.videoSlots.length === 0) return;

      var lastVisiblePanel = this.firstVisiblePanel + this.visiblePanelCount;
      var conditions = {
        sectionVisible: this.sectionInViewport,
        documentHidden: Boolean(global.document.hidden),
        reducedMotion: Boolean(this.motionQuery?.matches),
        saveData: prefersReducedData(),
      };

      this.videoSlots.forEach((slot) => {
        conditions.panelVisible = slot.panelIndex >= this.firstVisiblePanel && slot.panelIndex < lastVisiblePanel;
        if (shouldPlayVideo(conditions)) {
          this.playVideoSlot(slot);
        } else {
          this.pauseVideoSlot(slot);
        }
      });
    }

    playVideoSlot(slot) {
      if (!slot.video) this.mountVideoSlot(slot);
      if (!slot.video || !slot.video.paused) return;

      var playback = slot.video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch(() => {
          if (slot.panelIndex === this.firstVisiblePanel) this.syncAutoplay();
        });
      }
    }

    pauseVideoSlot(slot) {
      if (slot.video && !slot.video.paused) slot.video.pause();
    }

    mountVideoSlot(slot) {
      var fragment = slot.template.content?.cloneNode(true);
      var video = fragment?.querySelector('video');
      if (!video) return;

      Array.prototype.forEach.call(video.querySelectorAll('img'), (fallback) => fallback.remove());
      video.removeAttribute('controls');
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('disableremoteplayback', '');
      video.setAttribute('aria-hidden', 'true');
      video.addEventListener('playing', this.handleVideoPlaying, { once: true });
      slot.template.after(video);
      video.muted = true;
      video.defaultMuted = true;
      slot.video = video;
    }

    unmountVideos() {
      this.videoSlots.forEach((slot) => {
        var video = slot.video;
        if (!video) return;

        video.removeEventListener('playing', this.handleVideoPlaying);
        video.pause();
        Array.prototype.forEach.call(video.querySelectorAll('source'), (source) => source.remove());
        video.load();
        video.remove();
        slot.video = null;
      });
      this.videoSlots = [];
    }

    handleVideoPlaying(event) {
      var video = event.currentTarget;
      video?.setAttribute('data-chcs-video-ready', '');

      var isPrincipal = this.videoSlots.some((slot) => slot.video === video && slot.panelIndex === this.firstVisiblePanel);
      if (isPrincipal && this.videoSyncEnabled) this.syncAutoplay();
    }

    handleBlockSelect(event) {
      var panel = event.target?.closest?.('[data-chcs-panel]');
      var panelIndex = this.panels.indexOf(panel);
      if (panelIndex === -1) return;

      this.setTransientPause('editorSelection', true, false);
      var lastVisiblePanel = this.firstVisiblePanel + this.visiblePanelCount;
      if (panelIndex < this.firstVisiblePanel || panelIndex >= lastVisiblePanel) {
        this.go(getPanelPosition(panelIndex, this.getPerView(), this.scrollByPage), {
          wrap: false,
          instant: Boolean(event.detail?.load),
        });
      }
      this.syncAutoplay();
    }

    handleBlockDeselect() {
      this.setTransientPause('editorSelection', false);
    }
  }

  global.CmxHomeCollageSlider = {
    Element: CmxHomeCollageSliderElement,
    helpers: {
      getAutoplayDelay: getAutoplayDelay,
      getIndexAfterResize: getIndexAfterResize,
      getDragStepCount: getDragStepCount,
      getMaxIndex: getMaxIndex,
      getPanelPosition: getPanelPosition,
      getPerView: getPerView,
      isHorizontalSwipe: isHorizontalSwipe,
      normalizeIndex: normalizeIndex,
      shouldAutoplay: shouldAutoplay,
      shouldPlayVideo: shouldPlayVideo,
    },
  };

  if (!global.customElements.get('cmx-home-collage-slider')) {
    global.customElements.define('cmx-home-collage-slider', CmxHomeCollageSliderElement);
  }
})(typeof window !== 'undefined' ? window : globalThis);
